import { WAD } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import BN from "bn.js"

export const SLOTS_PER_YEAR = 63072000

type ProtocolTakeRate = number // Range: [0, 1]
type BorrowedAmount = BigNumber
type AvailableAmount = BigNumber
type UtilizationRate = number // Range: [0, 1]
type BorrowAPY = number // Range: [0, 1]

export type Configuration = {
  protocolTakeRate: ProtocolTakeRate
  isShowingAPY: boolean
  borrowedAmount: BorrowedAmount
  availableAmount: AvailableAmount
  optimalUtilizationRate: UtilizationRate
  maxUtilizationRate: UtilizationRate
  minBorrowRate: BorrowAPY // Min <= Optimal <= Max
  optimalBorrowRate: BorrowAPY // Min <= Optimal <= Max
  maxBorrowRate: BorrowAPY // Min <= Optimal <= Max
  superMaxBorrowRate: BorrowAPY // Min <= Optimal <= Max <= Super Max
  accumulatedProtocolFees: BN
  decimals: number
}

const calculateSupplyAPR = (config: Configuration) => {
  const currentUtilization = calculateUtilizationRatio(config)

  const borrowAPR = calculateBorrowAPR(config)
  const protocolTakePercentage = BigNumber(1).minus(config.protocolTakeRate)

  return currentUtilization.times(borrowAPR).times(protocolTakePercentage)
}

export const calculateUtilizationRatio = (config: Configuration) => {
  /* const borrowedAmount = new BigNumber(
      config.borrowedAmount.toString()
    ).shiftedBy(-18); */
  const borrowedAmount = config.borrowedAmount
  const totalSupply = borrowedAmount
    .plus(config.availableAmount.toString())
    .minus(new BigNumber(config.accumulatedProtocolFees.toString()).div(WAD).div(10 ** config.decimals))
  if (borrowedAmount.isZero() || totalSupply.isZero()) {
    return new BigNumber(0)
  }

  const denominator = borrowedAmount.plus(config.availableAmount.toString())
  const currentUtilization = borrowedAmount.dividedBy(denominator)

  return currentUtilization
}

const calculateBorrowAPR = (config: Configuration) => {
  const currentUtilization = calculateUtilizationRatio(config)
  const optimalUtilization = new BigNumber(config.optimalUtilizationRate)
  const maxUtilizationRate = new BigNumber(config.maxUtilizationRate)
  let borrowAPR
  if (currentUtilization.isLessThanOrEqualTo(optimalUtilization)) {
    const minBorrowRate = new BigNumber(config.minBorrowRate)
    if (optimalUtilization.isEqualTo(0)) {
      return minBorrowRate
    }
    const normalizedFactor = currentUtilization.dividedBy(optimalUtilization)

    const optimalBorrowRate = new BigNumber(config.optimalBorrowRate)

    borrowAPR = normalizedFactor.times(optimalBorrowRate.minus(minBorrowRate)).plus(minBorrowRate)
  } else if (currentUtilization.isLessThanOrEqualTo(maxUtilizationRate)) {
    const weight = currentUtilization.minus(optimalUtilization).dividedBy(maxUtilizationRate.minus(optimalUtilization))

    const optimalBorrowRate = new BigNumber(config.optimalBorrowRate)
    const maxBorrowRate = new BigNumber(config.maxBorrowRate)

    borrowAPR = weight.times(maxBorrowRate.minus(optimalBorrowRate)).plus(optimalBorrowRate)
  } else {
    const weight = currentUtilization.minus(maxUtilizationRate).dividedBy(new BigNumber(1).minus(maxUtilizationRate))

    const maxBorrowRate = new BigNumber(config.maxBorrowRate)
    const superMaxBorrowRate = new BigNumber(config.superMaxBorrowRate)

    borrowAPR = weight.times(superMaxBorrowRate.minus(maxBorrowRate)).plus(maxBorrowRate)
  }

  return borrowAPR
}

const calculateSupplyAPY = (config: Configuration) => {
  const apr = calculateSupplyAPR(config)
  const apy = new BigNumber(1).plus(new BigNumber(apr).dividedBy(SLOTS_PER_YEAR)).toNumber() ** SLOTS_PER_YEAR - 1
  return new BigNumber(apy)
}

const calculateBorrowAPY = (config: Configuration) => {
  const apr = calculateBorrowAPR(config)
  const apy = new BigNumber(1).plus(new BigNumber(apr).dividedBy(SLOTS_PER_YEAR)).toNumber() ** SLOTS_PER_YEAR - 1

  return new BigNumber(apy)
}

export const calculateSupplyInterest = (config: Configuration) =>
  config.isShowingAPY ? calculateSupplyAPY(config) : calculateSupplyAPR(config)

export const calculateBorrowInterest = (config: Configuration) =>
  config.isShowingAPY ? calculateBorrowAPY(config) : calculateBorrowAPR(config)
